const STORAGE_PREFIX = "mozzoapp:cash-register:";
const SESSION_STORAGE_PREFIX = "mozzoapp:cash-register-session:";
const inMemoryCashRegisterStore = new Map();

export const CASH_REGISTER_UPDATED_EVENT = "mozzoapp:cash-register-updated";
export const CASH_REGISTER_REMOTE_SYNC_SOURCE = "remote-sync";

export const buildCashRegisterStorageKey = (restaurantId) =>
  `${STORAGE_PREFIX}${restaurantId || "default"}`;

const buildCashRegisterSessionStorageKey = (restaurantId) =>
  `${SESSION_STORAGE_PREFIX}${restaurantId || "default"}`;

const createEmptyCashRegisterState = () => ({
  sessions: [],
});

const toSafeText = (value, maxLength = 240) =>
  String(value || "")
    .trim()
    .slice(0, maxLength);

const toSafeNumber = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const roundMoney = (value) => Math.round(toSafeNumber(value) * 100) / 100;

const buildMovementId = () =>
  `cash-movement-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;

const buildSessionId = () =>
  `cash-session-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;

const normalizeMovement = (movement) => {
  const kind = movement?.kind === "expense" ? "expense" : "income";

  return {
    id: movement?.id || buildMovementId(),
    kind,
    amount: roundMoney(Math.abs(movement?.amount || 0)),
    note: toSafeText(movement?.note, 220),
    reason: toSafeText(movement?.reason, 120),
    createdBy: toSafeText(movement?.createdBy, 120),
    createdAt: movement?.createdAt || new Date().toISOString(),
  };
};

const normalizeSession = (session) => {
  const status = session?.status === "closed" ? "closed" : "open";

  return {
    id: session?.id || buildSessionId(),
    status,
    openedAt: session?.openedAt || new Date().toISOString(),
    openedBy: toSafeText(session?.openedBy, 120),
    openingAmount: roundMoney(session?.openingAmount),
    openingNote: toSafeText(session?.openingNote, 220),
    closedAt: status === "closed" ? session?.closedAt || new Date().toISOString() : null,
    closedBy: toSafeText(session?.closedBy, 120),
    countedClosingAmount:
      status === "closed" && session?.countedClosingAmount !== null
        ? roundMoney(session?.countedClosingAmount)
        : status === "closed"
          ? roundMoney(session?.countedClosingAmount)
          : null,
    closingNote: toSafeText(session?.closingNote, 220),
    movements: Array.isArray(session?.movements)
      ? session.movements.map(normalizeMovement)
      : [],
  };
};

export const normalizeCashRegisterState = (state) => {
  const sessions = (Array.isArray(state?.sessions) ? state.sessions : [])
    .map(normalizeSession)
    .sort(
      (left, right) =>
        new Date(right.openedAt || 0).getTime() - new Date(left.openedAt || 0).getTime()
    );

  return { sessions };
};

const emitCashRegisterUpdate = (restaurant, state, source = "local") => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(CASH_REGISTER_UPDATED_EVENT, {
      detail: {
      restaurantId: restaurant?.id || "default",
      state,
      source,
      },
    })
  );
};

const isSettledPaymentOperation = (operation) => {
  const status = String(operation?.status || "").trim().toLowerCase();
  return status === "paid" || status === "closed";
};

const isCashPaymentOperation = (operation) => {
  const method = String(
    operation?.selectedMethod || operation?.requestedMethod || ""
  )
    .trim()
    .toLowerCase();

  return method === "efectivo" || method === "cash";
};

const getPaymentTimestamp = (operation) =>
  operation?.paidAt || operation?.closedAt || operation?.orderedAt || null;

const isWithinSessionWindow = (timestamp, session) => {
  if (!timestamp) {
    return false;
  }

  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  const openedAt = new Date(session?.openedAt || 0);
  const closedAt = session?.closedAt ? new Date(session.closedAt) : null;

  if (Number.isNaN(date.getTime()) || Number.isNaN(openedAt.getTime())) {
    return false;
  }

  if (date < openedAt) {
    return false;
  }

  if (closedAt && !Number.isNaN(closedAt.getTime()) && date > closedAt) {
    return false;
  }

  return true;
};

const getMovementSignedAmount = (movement) =>
  movement?.kind === "expense"
    ? -roundMoney(movement?.amount)
    : roundMoney(movement?.amount);

const enrichSession = (session, paymentOperations = [], nowDate = new Date()) => {
  const cashOperations = (Array.isArray(paymentOperations) ? paymentOperations : []).filter(
    (operation) =>
      isSettledPaymentOperation(operation) &&
      isCashPaymentOperation(operation) &&
      isWithinSessionWindow(getPaymentTimestamp(operation), session)
  );
  const cashSalesAmount = roundMoney(
    cashOperations.reduce(
      (sum, operation) => sum + roundMoney(operation?.grossAmount ?? operation?.amount),
      0
    )
  );
  const manualIncomeAmount = roundMoney(
    (session?.movements || [])
      .filter((movement) => movement.kind === "income")
      .reduce((sum, movement) => sum + roundMoney(movement.amount), 0)
  );
  const manualExpenseAmount = roundMoney(
    (session?.movements || [])
      .filter((movement) => movement.kind === "expense")
      .reduce((sum, movement) => sum + roundMoney(movement.amount), 0)
  );
  const manualNetAmount = roundMoney(
    (session?.movements || []).reduce(
      (sum, movement) => sum + getMovementSignedAmount(movement),
      0
    )
  );
  const expectedAmount = roundMoney(
    roundMoney(session?.openingAmount) + cashSalesAmount + manualNetAmount
  );
  const countedClosingAmount =
    session?.countedClosingAmount === null || session?.countedClosingAmount === undefined
      ? null
      : roundMoney(session.countedClosingAmount);
  const differenceAmount =
    countedClosingAmount === null
      ? null
      : roundMoney(countedClosingAmount - expectedAmount);

  return {
    ...session,
    cashSalesAmount,
    cashSalesCount: cashOperations.length,
    cashOperations,
    manualIncomeAmount,
    manualExpenseAmount,
    manualNetAmount,
    expectedAmount,
    countedClosingAmount,
    differenceAmount,
    updatedAt:
      session?.closedAt ||
      session?.movements?.[session.movements.length - 1]?.createdAt ||
      session?.openedAt ||
      nowDate.toISOString(),
  };
};

export const getCashRegisterState = (restaurant) => {
  const storageKey = buildCashRegisterStorageKey(restaurant?.id);
  const sessionStorageKey = buildCashRegisterSessionStorageKey(restaurant?.id);

  if (typeof window === "undefined") {
    return normalizeCashRegisterState(createEmptyCashRegisterState());
  }

  if (inMemoryCashRegisterStore.has(storageKey)) {
    return normalizeCashRegisterState(inMemoryCashRegisterStore.get(storageKey));
  }

  try {
    const rawValue =
      window.sessionStorage.getItem(sessionStorageKey) ||
      window.localStorage.getItem(storageKey);

    if (!rawValue) {
      return normalizeCashRegisterState(createEmptyCashRegisterState());
    }

    const parsedValue = JSON.parse(rawValue);
    const normalizedState = normalizeCashRegisterState(parsedValue);
    inMemoryCashRegisterStore.set(storageKey, normalizedState);
    return normalizedState;
  } catch (error) {
    console.error("No pudimos leer el estado de caja:", error);
    return normalizeCashRegisterState(
      inMemoryCashRegisterStore.get(storageKey) || createEmptyCashRegisterState()
    );
  }
};

export const saveCashRegisterState = (restaurant, state, options = {}) => {
  const storageKey = buildCashRegisterStorageKey(restaurant?.id);
  const sessionStorageKey = buildCashRegisterSessionStorageKey(restaurant?.id);
  const normalizedState = normalizeCashRegisterState(state);
  const serializedPayload = JSON.stringify(normalizedState);

  try {
    if (window.localStorage.getItem(storageKey) === serializedPayload) {
      return normalizedState;
    }

    window.localStorage.setItem(storageKey, serializedPayload);
    try {
      window.sessionStorage.setItem(sessionStorageKey, serializedPayload);
    } catch (sessionError) {
      console.warn("No pudimos espejar la caja en sessionStorage:", sessionError);
    }

    inMemoryCashRegisterStore.set(storageKey, normalizedState);
    emitCashRegisterUpdate(
      restaurant,
      normalizedState,
      options?.eventSource || "local"
    );
  } catch (error) {
    console.error("No pudimos guardar el estado de caja:", error);
    inMemoryCashRegisterStore.set(storageKey, normalizedState);
    emitCashRegisterUpdate(
      restaurant,
      normalizedState,
      options?.eventSource || "local"
    );
  }

  return normalizedState;
};

export const getActiveCashSession = (state) =>
  normalizeCashRegisterState(state).sessions.find((session) => session.status === "open") ||
  null;

export const buildCashRegisterSnapshot = (
  state,
  paymentOperations = [],
  nowDate = new Date()
) => {
  const normalizedState = normalizeCashRegisterState(state);
  const enrichedSessions = normalizedState.sessions.map((session) =>
    enrichSession(session, paymentOperations, nowDate)
  );
  const activeSession =
    enrichedSessions.find((session) => session.status === "open") || null;
  const closedSessions = enrichedSessions.filter((session) => session.status === "closed");
  const coveredOperationIds = new Set();

  enrichedSessions.forEach((session) => {
    session.cashOperations.forEach((operation) => {
      if (operation?.id) {
        coveredOperationIds.add(operation.id);
      }
    });
  });

  const uncoveredCashOperations = (Array.isArray(paymentOperations) ? paymentOperations : [])
    .filter(
      (operation) =>
        isSettledPaymentOperation(operation) &&
        isCashPaymentOperation(operation) &&
        operation?.id &&
        !coveredOperationIds.has(operation.id)
    )
    .sort(
      (left, right) =>
        new Date(getPaymentTimestamp(right) || 0).getTime() -
        new Date(getPaymentTimestamp(left) || 0).getTime()
    );

  return {
    activeSession,
    sessions: enrichedSessions,
    recentClosedSessions: closedSessions.slice(0, 6),
    uncoveredCashOperations,
    uncoveredCashAmount: roundMoney(
      uncoveredCashOperations.reduce(
        (sum, operation) => sum + roundMoney(operation?.grossAmount ?? operation?.amount),
        0
      )
    ),
  };
};
